import React, { useEffect, useState } from 'react';
import { Accordion, Text } from '@mantine/core';
import { motion } from 'framer-motion';

import { useMachinesList } from '../../../hooks/useUserMachines';
import { staggerContainer, fadeInFromTop, fadeInFromLeft, fadeInFromRight, createStaggerContainer, fadeIn } from '../../../utils/animations';
import MachineAccordionItem from './MachineAccordionItem';
import colors from '../../../utils/colors';
import { useLoadingOverlay } from '../../../components/LoadingOverlayContext';
import { useLanguage } from '../../../context/LanguageContext';
import ScrollAnimatedSection from '../../../components/ScrollAnimatedSection';

interface MachinesListProps {
  onLoadingComplete?: (isComplete: boolean) => void;
}

const MachinesList: React.FC<MachinesListProps> = ({ onLoadingComplete }) => {
  const { data: machinesData, isLoading, error } = useMachinesList();
  const { setLoading } = useLoadingOverlay();
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const { translate } = useLanguage();

  useEffect(() => {
    setLoading(isLoading);
    
    if (!isLoading && machinesData) {
      setTimeout(() => {
        setShouldAnimate(true);
        onLoadingComplete?.(true);
      }, 50);
    } else {
      onLoadingComplete?.(false);
    }
  }, [isLoading, machinesData, setLoading, onLoadingComplete]);

  useEffect(() => {
    if (!isLoading && (error || !machinesData?.machines?.length)) {
      onLoadingComplete?.(true);
    }
  }, [isLoading, error, machinesData?.machines?.length, onLoadingComplete]);

  if (isLoading) {
    return null;
  }

  if (error || !machinesData?.machines?.length) {
    return (
      <motion.div
        className="mb-6"
        variants={staggerContainer}
        initial="hidden"
        animate={shouldAnimate ? "visible" : "hidden"}
      >
        <motion.div variants={fadeInFromLeft} className="mb-2">
          <Text 
            size="xl" 
            fw={700} 
            c={colors.custom36s} 
            className="text-center"
          >
            {translate('yourMachines')}
          </Text>
        </motion.div>

        <motion.div variants={fadeInFromLeft}>
          <Text size="md" ta="center" c="dimmed">
            {translate('noMachines')}
          </Text>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="mb-6"
      variants={staggerContainer}
      initial="hidden"
      animate={shouldAnimate ? "visible" : "hidden"}
    >
      <motion.div variants={fadeInFromLeft} className="mb-2 mt-2">
        <Text 
          size="lg" 
          fw={500} 
          c={colors.custom36s} 
          className="text-center"
        >
          {translate('yourMachines')}
        </Text>
      </motion.div>

      <motion.div className="overflow-x-hidden"
        variants={createStaggerContainer(0.15, 0.1)}
        initial="hidden"
        animate={shouldAnimate ? "visible" : "hidden"}
      >
        <Accordion 
          variant="separated" 
          radius="xs"
          multiple
          defaultValue={machinesData.machines.map(machine => `machine-${machine.id}`)} //defaultowo otwarte
        >
          
          {machinesData.machines.map((machine, index) => {
            if (index < 4) {
              // First 4 machines use stagger animation
              return (
                <motion.div
                  key={machine.id}
                  variants={index % 2 === 0 ? fadeInFromLeft : fadeInFromLeft}
                  className="overflow-x-hidden"
                >
                  <MachineAccordionItem 
                    machine={machine}
                    index={index}
                  />
                </motion.div>
              );
            }
            
            // Remaining machines use scroll animation
            return (
              <div key={machine.id} className="overflow-x-hidden">
                <ScrollAnimatedSection 
                  variants={index % 2 === 0 ? fadeInFromLeft : fadeInFromLeft}
                  viewAmount={0.5}
                  delay={0.1}
                >
                  <MachineAccordionItem 
                    machine={machine}
                    index={index}
                  />
                </ScrollAnimatedSection>
              </div>
            );
          })}
        </Accordion>
      </motion.div>
    </motion.div>
  );
};

export default MachinesList;